import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import Stepper from '@material-ui/core/Stepper';
import React, { useState, useMemo, useCallback } from 'react';
import { useNotify } from 'react-admin';

import DialogTitleWithCloseButton from '../../../core/components/DialogTitleWithCloseButton';
import Confirm from '../../../core/components/forked/layout/Confirm';
import SelectInvestorsStep from './SelectInvestorsStep';
import PropertiesStep from './PropertiesStep';
import { CopyInvestorContext } from './CopyInvestorContext';
//import { StepConnector } from '@material-ui/core';
import useNotifyServerError from '../../../core/hooks/useNotifyServerError';
import { makeStyles } from '@material-ui/core';

const useStyles = makeStyles({
  stepper: {
    backgroundColor: '#FFFFFF',
    padding: '0 0 0px',
    display: 'inline-flex'
  },
  leftWrapper: {
    flexGrow: 1
  },
  button: {
    '& span': {
      padding: 0
    }
  },
  backButton: {
    border: '1px solid #E0E0E0',
    '& span': {
      padding: 0
    }
  },
  saveButton: {
    border: '1px solid #E0E0E0',
    '& span': {
      padding: 0
    }
  },
  root: {
    paddingTop: '10px',
    paddingBottom: '10px'
  },
  paperWidthFalse: {
    height: '80vh',
    mminHeight: '80vh',
    width: '100%'
  },
  dialogContent: {
    padding: '0px',
    display: 'flex',
    flexDirection: 'column',
    height: '80vh'
  },
  stepperContainer: {
    backgroundColor: '#FFFFFF',
    padding: '11px 8px',
    display: 'flex',
    borderBottom: '1px solid #E0E0E0'
  },
  stepContainer: {
    padding: '16px',
    display: 'flex',
    '& .simple-form': {
      display: 'flex',
      maxHeight: '100%',
      width: '100%',
      '& > div': {
        width: '100%'
      }
    }
  }
});

const CopyToInvestorStepper = props => {
  const [activeStep, setActiveStep] = useState(0);
  const [confirmDialogIsOpen, setConfirmDialogIsOpen] = useState(false);
  const [investorContext, setInvestorsContext] = useState({});
  const providerInvestorContext = useMemo(
    () => ({ investorContext, setInvestorsContext }),
    [investorContext, setInvestorsContext]
  );
  const notify = useNotify();
  const notifyServerError = useNotifyServerError();

  const classes = useStyles();
  const { showDialog, handleDialogToggle, handleRefresh, ...rest } = props;
  // List of steps
  const steps = useMemo(
    () => [
      {
        name: 'investors',
        label: 'Select Investor',
        component: SelectInvestorsStep,
        nextButton: true,
        backButton: false,
        shouldDisplay: true
      },
      {
        name: 'properties',
        label: 'Edit Document Properties',
        component: PropertiesStep,
        nextButton: false,
        backButton: true,
        shouldDisplay: true
      }
    ],
    []
  );

  const hasNext = steps[activeStep] ? steps[activeStep].nextButton : false;
  const hasBack = steps[activeStep] ? steps[activeStep].backButton : false;

  const handleCloseClick = useCallback(() => {
    setConfirmDialogIsOpen(true);
  }, []);

  const handleConfirmDialogClose = useCallback(() => {
    setConfirmDialogIsOpen(false);
  }, []);

  const handleClose = useCallback(() => {
    setConfirmDialogIsOpen(false);
    handleDialogToggle();
  }, [handleDialogToggle]);

  const onSubmitEnd = useCallback(
    async values => {
      if (activeStep === 1) {
        //prepare data for new api here or maybe better on submit of second step and here just close
        try {
          notify('ra.notification.created');
          handleRefresh(values);
          handleClose();
        } catch (e) {
          notifyServerError(e);
        }
      } else {
        setActiveStep(activeStep + 1);
      }
    },
    [activeStep, handleClose, notify, notifyServerError, handleRefresh]
  );

  const getActiveStep = useCallback(() => {
    const step = steps[activeStep];
    return (
      <step.component {...rest} onSubmitEnd={onSubmitEnd} variant="standard" />
    );
  }, [activeStep, onSubmitEnd, rest, steps]);

  const handleNextForm = useCallback(() => {
    // Trigger a submit to active form
    // This is needed because our modal action buttons are oustide the form
    investorContext.currentForm && investorContext.currentForm.submit();
  }, [investorContext.currentForm]);

  const handleBackForm = useCallback(() => {
    if (activeStep > 0) {
      setInvestorsContext({ ...investorContext, currentForm: undefined });
      setActiveStep(activeStep - 1);
    }
  }, [activeStep, investorContext]);
  return (
    <Dialog
      open={showDialog}
      aria-label="Copy To Another Investor"
      fullWidth={true}
      maxWidth="xl"
      onClose={(event, reason) => {
        if (reason === 'backdropClick') return null;
        if (reason === 'escapeKeyDown') return handleCloseClick();
      }}
      classes={{
        paperWidthFalse: classes.paperWidthFalse // class name, e.g. `classes-nesting-root-x`
      }}
    >
      <DialogTitleWithCloseButton onClose={handleCloseClick}>
        Copy To Another Investor
      </DialogTitleWithCloseButton>
      <DialogContent className={classes.dialogContent}>
        <>
          <div className={classes.stepperContainer}>
            <Stepper
              activeStep={activeStep}
              className={classes.stepper}
              //connector={ids.length === 0 ? <></> : <StepConnector />}
            >
              {steps.map(step => (
                <Step key={step.name}>
                  {step.shouldDisplay && <StepLabel>{step.label}</StepLabel>}
                </Step>
              ))}
            </Stepper>
          </div>
          <div className={classes.stepContainer}>
            <CopyInvestorContext.Provider value={providerInvestorContext}>
              {getActiveStep(steps)}
            </CopyInvestorContext.Provider>
          </div>
          <Confirm
            isOpen={confirmDialogIsOpen}
            title="Close Window?"
            content={
              <div>
                <p style={{ margin: 0 }}>
                  You are about to quit the create operation, and return to the
                  Edit Properties page.
                </p>
                <br />
                Your progress on this step will not be saved.
              </div>
            }
            onConfirm={handleClose}
            onClose={handleConfirmDialogClose}
            confirm="ra.action.close"
          />
        </>
      </DialogContent>
      <DialogActions style={{ borderTop: `1px solid rgba(0, 0, 0, 0.12)` }}>
        <div className={classes.leftWrapper}>
          {hasBack && (
            <Button
              onClick={handleBackForm}
              variant="outlined"
              color="primary"
              className={classes.backButton}
            >
              Back
            </Button>
          )}
        </div>
        <Button
          onClick={handleClose}
          color="primary"
          variant="outlined"
          className={classes.button}
        >
          Cancel
        </Button>
        <Button
          onClick={handleNextForm}
          color="primary"
          variant="contained"
          className={classes.button}
          loading={props.loading}
        >
          {hasNext ? 'Next' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CopyToInvestorStepper;
